import * as THREE from "three";
import type ThreeGlobe from "three-globe";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import Experience from "./Experience";
import Sizes from "./utils/Sizes";

export default class Camera {
  experience: Experience;
  sizes: Sizes;
  scene: THREE.Scene;
  canvas: HTMLElement;
  instance: THREE.PerspectiveCamera;
  controls: OrbitControls;

  properties = {
    position: {
      x: 0,
      y: 0,
      z: 10, // Start at 2D-friendly position
    },
    fov: 45,
    near: 0.01,
    far: 3000,
  };

  constructor() {
    this.experience = new Experience();
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;

    this.instance = new THREE.PerspectiveCamera(
      this.properties.fov,
      this.sizes.width / this.sizes.height,
      this.properties.near,
      this.properties.far
    );

    this.controls = new OrbitControls(this.instance, this.canvas);
    this.setInstance();
    this.setControls();
  }

  setInstance() {
    this.instance.position.set(
      this.properties.position.x,
      this.properties.position.y,
      this.properties.position.z
    );
    this.scene.add(this.instance);
  }

  setControls() {
    this.controls.enabled = false; // Disabled by default
  }

  enableOrbitControls(globe: ThreeGlobe) {
    const R = globe.getGlobeRadius();

    this.controls.enabled = true;

    // Set zoom limits based on globe

    this.controls.rotateSpeed = 2;
    this.controls.zoomSpeed = 0.8;
    this.controls.minDistance =
      R * (1 + 5 / 2 ** globe.globeTileEngineMaxLevel()); // Just above surface, adjusted resolution to max level
    this.controls.maxDistance = this.instance.far - R;

    // Disable panning entirely to prevent camera position changes
    this.controls.enablePan = false;

    // Configure touch gestures
    // ONE finger: Rotate
    // TWO fingers: Dolly (Zoom) + Rotate (no panning)
    this.controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    };

    this.controls.update();

    // Update pov when camera moves
    globe.setPointOfView(this.instance);
    this.controls.addEventListener("change", () => {
      globe.setPointOfView(this.instance);
      const distToSurface =
        this.instance.position.distanceTo(globe.position) - R;
      this.controls.rotateSpeed = (distToSurface / R) * 0.5;
      this.controls.zoomSpeed = Math.sqrt(distToSurface / R) * 0.5;
    });
  }

  getVisibleSizeAtZ(zWorld = 0) {
    // distance from camera to the Z plane you draw on (your S is at z=0)
    const dist = Math.abs(this.instance.position.z - zWorld);
    const vFov = THREE.MathUtils.degToRad(this.instance.fov); // vertical FOV in radians
    const height = 2 * Math.tan(vFov / 2) * dist;
    const width = height * this.instance.aspect;
    return { width, height };
  }

  resize() {
    this.instance.aspect = this.sizes.width / this.sizes.height;
    this.instance.updateProjectionMatrix();
  }

  update() {
    this.controls.update();
  }
}
